import React, { useState } from 'react';
import styled from 'styled-components';
import { semanticColors } from '@/common/styles/colorTokens';
import { typography } from '@/common/styles/typographyTokens';
import { Button } from '@/common/components/button/Button';
import { SignUpData } from '../SignUp';
import TermsModal from '../components/TermsModal';
import { navigateBackOrClose } from '@/common/utils/commonUtil';
import { useNavigate } from 'react-router-dom';

/**
 * 1단계: 약관 동의 컴포넌트
 */

interface TermsStepProps {
  data: SignUpData;
  onNext: (data: Partial<SignUpData>) => void;
}

const TermsStep: React.FC<TermsStepProps> = ({ data, onNext }) => {
  const navigate = useNavigate();
  const [agreements, setAgreements] = useState({
    termsAgreed: data.termsAgreed,
    privacyAgreed: data.privacyAgreed,
    marketingAgreed: data.marketingAgreed,
  });

  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    type: 'terms' | 'privacy' | 'marketing' | null;
  }>({
    isOpen: false,
    type: null,
  });

  /**
   * 전체 동의 체크박스 핸들러
   */
  const handleAllAgree = (checked: boolean) => {
    setAgreements({
      termsAgreed: checked,
      privacyAgreed: checked,
      marketingAgreed: checked,
    });
  };

  /**
   * 개별 동의 체크박스 핸들러
   */
  const handleIndividualAgree = (key: keyof typeof agreements, checked: boolean) => {
    setAgreements(prev => ({
      ...prev,
      [key]: checked,
    }));
  };

  /**
   * 약관 모달 열기
   */
  const handleOpenModal = (type: 'terms' | 'privacy' | 'marketing') => {
    setModalState({
      isOpen: true,
      type,
    });
  };

  /**
   * 약관 모달 닫기
   */
  const handleCloseModal = () => {
    setModalState({
      isOpen: false,
      type: null,
    });
  };

  /**
   * 다음 단계로 진행
   */
  const handleNext = () => {
    if (!agreements.termsAgreed || !agreements.privacyAgreed) {
      alert('필수 약관에 동의해주세요.');
      return;
    }
    
    onNext(agreements);
  };

  const isAllAgreed = agreements.termsAgreed && agreements.privacyAgreed && agreements.marketingAgreed;
  const canProceed = agreements.termsAgreed && agreements.privacyAgreed;

  return (
    <Container>
      <TermsContainer>
        <AllAgreeSection>
          <CheckboxWrapper>
            <Checkbox
              type="checkbox"
              id="allAgree"
              checked={isAllAgreed}
              onChange={(e) => handleAllAgree(e.target.checked)}
            />
            <CheckboxLabel htmlFor="allAgree" $isMain>
              전체 동의
            </CheckboxLabel>
          </CheckboxWrapper>
        </AllAgreeSection>

        <Divider />

        <TermsList>
          <TermsItem>
            <CheckboxWrapper>
              <Checkbox
                type="checkbox"
                id="terms"
                checked={agreements.termsAgreed}
                onChange={(e) => handleIndividualAgree('termsAgreed', e.target.checked)}
              />
              <CheckboxLabel htmlFor="terms">
                <Required>[필수]</Required> 서비스 이용약관 동의
              </CheckboxLabel>
            </CheckboxWrapper>
            <ViewButton type="button" onClick={() => handleOpenModal('terms')}>
              보기
            </ViewButton>
          </TermsItem>

          <TermsItem>
            <CheckboxWrapper>
              <Checkbox
                type="checkbox"
                id="privacy"
                checked={agreements.privacyAgreed}
                onChange={(e) => handleIndividualAgree('privacyAgreed', e.target.checked)}
              />
              <CheckboxLabel htmlFor="privacy">
                <Required>[필수]</Required> 개인정보 처리방침 동의
              </CheckboxLabel>
            </CheckboxWrapper>
            <ViewButton type="button" onClick={() => handleOpenModal('privacy')}>
              보기
            </ViewButton>
          </TermsItem>

          {/* <TermsItem>
            <CheckboxWrapper>
              <Checkbox
                type="checkbox"
                id="marketing"
                checked={agreements.marketingAgreed}
                onChange={(e) => handleIndividualAgree('marketingAgreed', e.target.checked)}
              />
              <CheckboxLabel htmlFor="marketing">
                <Optional>[선택]</Optional> 마케팅 정보 수신 동의
              </CheckboxLabel>
            </CheckboxWrapper>
            <ViewButton type="button">보기</ViewButton>
          </TermsItem> */}
        </TermsList>
      </TermsContainer>

      <ButtonContainer>
        <Button
          $variant="outline"
          $size="md"
          $isFull
          onClick={() => {
            navigate('/login')
          }}
        >
          취소
        </Button>
        <Button
          $variant="gradient"
          $size="md"
          $isFull
          disabled={!canProceed}
          onClick={handleNext}
        >
          다음
        </Button>
      </ButtonContainer>

      {/* 약관 모달 */}
      {modalState.type && (
        <TermsModal
          isOpen={modalState.isOpen}
          type={modalState.type}
          onClose={handleCloseModal}
        />
      )}
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: center;
`;

const Title = styled.h2`
  ${typography.heading.h2}
  color: ${semanticColors.text.primary};
  margin: 0;
`;

const Subtitle = styled.p`
  ${typography.subtitle.subtitle2}
  color: ${semanticColors.text.secondary};
  margin: 0;
`;

const TermsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 8px;
  background-color: ${semanticColors.background.secondary};
`;

const AllAgreeSection = styled.div`
  padding: 16px 0;
`;

const Divider = styled.hr`
  border: none;
  height: 1px;
  background-color: ${semanticColors.border.primary};
  margin: 0;
`;

const TermsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TermsItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const CheckboxLabel = styled.label<{ $isMain?: boolean }>`
  ${props => props.$isMain ? typography.subtitle.subtitle1 : typography.paragraph.p1.default}
  color: ${semanticColors.text.primary};
  cursor: pointer;
  font-weight: ${props => props.$isMain ? 600 : 400};
`;

const Required = styled.span`
  color: ${semanticColors.background.danger.primary};
  font-weight: 500;
`;

const Optional = styled.span`
  color: ${semanticColors.text.secondary};
  font-weight: 500;
`;

const ViewButton = styled.button`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.tertiary};
  background: none;
  border: none;
  cursor: pointer;
  text-decoration: underline;
  
  &:hover {
    color: ${semanticColors.text.secondary};
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 8px;
`;

export default TermsStep;
